import { motion } from 'framer-motion';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, rem } from '@bonusly/design-system';
import { useIsBizyEnabledForUser } from '@bonusly/who-am-i';

import { Bizy } from '../../../../components/bizy';
import { useBizyAskClick } from '../../../../components/bizy-side-panel/hooks/use-bizy-ask-click';
import { useBizyUnreadCount, useHasBizyNotification } from '../../../../components/bizy-side-panel/hooks/use-has-bizy-notification';
import { useBizySidePanelStore } from '../../../../components/bizy-side-panel/store/use-bizy-side-panel-store';

export const AskBizyButton: FC = () => {
	const { t } = useTranslation('vertical_navbar');
	const isEnabled = useIsBizyEnabledForUser();
	const isOpen = useBizySidePanelStore((s) => s.isOpen);
	const hasUnread = useHasBizyNotification();
	const unreadCount = useBizyUnreadCount();
	const handleClick = useBizyAskClick();

	if (!isEnabled) return null;

	return (
		<motion.div
			initial={false}
			animate={{ height: isOpen ? 0 : 'auto', opacity: isOpen ? 0 : 1 }}
			transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
			style={{ overflow: 'hidden' }}
		>
			<Box pt={3}>
				<Button
					colorScheme="secondary"
					variant="ghost"
					borderRadius="sm"
					h="auto"
					w="100%"
					py={1}
					px={2}
					fontSize={50}
					fontWeight="medium"
					display="flex"
					alignItems="center"
					justifyContent="space-between"
					gap={2}
					onClick={handleClick}
					data-testid="ask-bizy-button"
					aria-label={hasUnread ? t('admin_menu.ask_bizy_with_notification', 'Ask Bizy — you have a new message') : undefined}
					_hover={{ bg: 'secondary.bgAlpha' }}
				>
					<Box display="inline-flex" alignItems="center" gap={2}>
						<motion.div
							animate={
								hasUnread
									? {
											rotate: [0, -12, 12, -12, 12, 0],
											scale: [1, 1.08, 1.08, 1.08, 1.08, 1],
										}
									: { rotate: 0, scale: 1 }
							}
							transition={hasUnread ? { duration: 1.4, repeat: Infinity, repeatDelay: 1.6, ease: 'easeInOut' } : { duration: 0.2 }}
							style={{ display: 'flex', transformOrigin: 'bottom center' }}
						>
							<Bizy state={hasUnread ? 'waving' : 'curious'} size={32} />
						</motion.div>
						{t('admin_menu.ask_bizy')}
					</Box>
					{hasUnread && (
						<Box
							minWidth={rem(18)}
							height={rem(18)}
							px={1.5}
							borderRadius="full"
							bg="red.500"
							color="white"
							display="inline-flex"
							alignItems="center"
							justifyContent="center"
							fontSize="xs"
							fontWeight="bold"
							lineHeight={1}
						>
							{unreadCount > 9 ? '9+' : unreadCount}
						</Box>
					)}
				</Button>
			</Box>
		</motion.div>
	);
};
